import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { getAccount } from '../api/admin'
import type { AdminAccountDetail } from '../api/adminTypes'
import { Card } from '../components/ui/Card'
import { StatusBadge } from './StatusBadge'
import { formatDateTime } from './format'
import { LoadingNote, ErrorNote } from './Notes'

export function AccountDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [account, setAccount] = useState<AdminAccountDetail | null>(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!id) return
    setAccount(null)
    setError(false)
    getAccount(id)
      .then(setAccount)
      .catch(() => setError(true))
  }, [id])

  if (error) return <ErrorNote>Account not found, or something went wrong loading it.</ErrorNote>
  if (!account) return <LoadingNote>Loading account…</LoadingNote>

  return (
    <div>
      <Link to="/accounts" style={{ color: 'var(--color-action-primary)', fontFamily: 'var(--font-body)', fontSize: 'var(--text-body-sm)', textDecoration: 'none' }}>
        ‹ Accounts
      </Link>

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-3)', margin: 'var(--spacing-3) 0 var(--spacing-6) 0' }}>
        <h1
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--text-title-lg)',
            fontWeight: 'var(--weight-semibold)',
            color: 'var(--color-text-primary)',
            margin: 0,
          }}
        >
          {account.email}
        </h1>
        <StatusBadge status={account.status} />
      </div>

      <Card padding="md" style={{ marginBottom: 'var(--spacing-6)' }}>
        <DetailRow label="Created" value={formatDateTime(account.created_at)} />
        <DetailRow label="Last login" value={formatDateTime(account.last_login_at)} />
        <DetailRow label="Locked at" value={account.locked_at ? formatDateTime(account.locked_at) : '—'} />
        <DetailRow label="Notes" value={account.notes ?? '—'} last />
      </Card>

      <SectionHeading>Sessions ({account.sessions.length})</SectionHeading>
      <Card padding={false} style={{ overflow: 'hidden', marginBottom: 'var(--spacing-6)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-body)' }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--color-outline-variant)' }}>
              <Th>Issued</Th>
              <Th>Expires</Th>
              <Th>Last seen</Th>
              <Th>IP</Th>
              <Th>Revoked</Th>
            </tr>
          </thead>
          <tbody>
            {account.sessions.length === 0 && (
              <tr><Td colSpan={5}>No sessions.</Td></tr>
            )}
            {account.sessions.map((s) => (
              <tr key={s.id} style={{ borderBottom: '1px solid var(--color-outline-subtle)' }}>
                <Td>{formatDateTime(s.issued_at)}</Td>
                <Td>{formatDateTime(s.expires_at)}</Td>
                <Td>{formatDateTime(s.last_seen_at)}</Td>
                <Td>{s.ip ?? '—'}</Td>
                <Td>{s.revoked ? 'Yes' : 'No'}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <SectionHeading>OTP history ({account.otps.length})</SectionHeading>
      <Card padding={false} style={{ overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-body)' }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--color-outline-variant)' }}>
              <Th>Issued</Th>
              <Th>Expires</Th>
              <Th>Consumed</Th>
              <Th>Attempts</Th>
            </tr>
          </thead>
          <tbody>
            {account.otps.length === 0 && (
              <tr><Td colSpan={4}>No OTPs issued.</Td></tr>
            )}
            {account.otps.map((o) => (
              <tr key={o.id} style={{ borderBottom: '1px solid var(--color-outline-subtle)' }}>
                <Td>{formatDateTime(o.issued_at)}</Td>
                <Td>{formatDateTime(o.expires_at)}</Td>
                <Td>{o.consumed ? 'Yes' : 'No'}</Td>
                <Td>{o.attempts}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  )
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2
      style={{
        fontFamily: 'var(--font-display)',
        fontSize: 'var(--text-title-sm)',
        fontWeight: 'var(--weight-semibold)',
        color: 'var(--color-text-primary)',
        margin: '0 0 var(--spacing-3) 0',
      }}
    >
      {children}
    </h2>
  )
}

function DetailRow({ label, value, last }: { label: string; value: string; last?: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: '8px 0',
        borderBottom: last ? 'none' : '1px solid var(--color-outline-subtle)',
        fontFamily: 'var(--font-body)',
        fontSize: 'var(--text-body-sm)',
      }}
    >
      <span style={{ color: 'var(--color-text-secondary)' }}>{label}</span>
      <span style={{ color: 'var(--color-text-primary)' }}>{value}</span>
    </div>
  )
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th style={{ padding: '10px 16px', fontSize: 'var(--text-label-sm)', fontWeight: 'var(--weight-semibold)', color: 'var(--color-text-secondary)' }}>
      {children}
    </th>
  )
}

function Td({ children, colSpan }: { children: React.ReactNode; colSpan?: number }) {
  return (
    <td colSpan={colSpan} style={{ padding: '12px 16px', fontSize: 'var(--text-body-sm)', color: 'var(--color-text-primary)' }}>
      {children}
    </td>
  )
}
